import {
  askForEmailChangeRequest,
  askForEmailPreferencesRequest,
  changePasswordRequest,
  getAllSessionsRequest,
  getCustomerProfileRequest,
  getNewPhoneOTPRequest,
  getOldPhoneOTPRequest,
  getOTPRequest,
  getPhoneVerificationOTP,
  logoutAllSessions,
  logoutCurrentDevice,
  logoutDevice,
  newUpdateEmailWithOTPRequest,
  unenrollFromPasswordlessRequest,
  updateCustomerProfileRequest,
  updateEmailWithOTPRequest,
  updateNewEmailPreferencesRequest,
  updateOldEmailPreferencesRequest,
  verifyNewPhoneOTPRequest,
  verifyOldPhoneOTPRequest,
  verifyOTPRequest,
  getOTPv2Request,
  passwordlessEnrollmentVerifyOTPRequest,
  submitWebAuthnEnrollmentRequest,
  getOtpOptionsRequest,
  updatePasswordWithOTPRequest,
} from "./requestCreator";
import { get, post, put } from "./requestWrapper";
import { getTMXSessionID } from "../utils";
import {RECAPTCHA_EVENT} from "../components/SignInSecurity/SignInDetails/EmailSection/constants";

export function getCustomerProfile() {
  const { url, headers } = getCustomerProfileRequest();
  return get(url, {}, headers);
}

export function changePassword({newPassword, currentPassword, account}) {
  const { url, data, headers } = changePasswordRequest(newPassword, currentPassword, account?.profile.emailId, account?.profile.emailId, account?.profile?.name?.firstName, account?.profile?.lastModifiedDate);
  return post(url, data, headers);
}

export function getOldPhoneOTP(emailId, phone, otpChannel, phoneVerified, sessionId) {
  const { url, headers } = getOldPhoneOTPRequest(emailId, phone, otpChannel, phoneVerified, sessionId);
  return get(url, {}, headers);
}

export function getNewPhoneOTP(emailId, phone, otpChannel, userId, sessionId, phoneVerified) {
  const { url, headers } = getNewPhoneOTPRequest(emailId, phone, otpChannel, userId, sessionId, phoneVerified);
  return get(url, {}, headers);
}

export function verifyOldPhoneOTP(otp, otpTicket, phoneNumber, account, autoToken) {
  otpTicket.phoneVerified = account.encryptVerifiedPhoneToken;
  return genericVerifyOTP(otp, otpTicket, phoneNumber, account, false, verifyOldPhoneOTPRequest, autoToken);
}

export function verifyNewPhoneOTP(otp, otpTicket, phoneNumber, account, autoToken) {
  return genericVerifyOTP(otp, otpTicket, phoneNumber, account, !!account.is2FA, verifyNewPhoneOTPRequest, autoToken); // Opt in to 2FA if user was previously opted in
}

export function askForOTPRequest(oldEmail, newEmail, phoneVerified, otpChannel, autoToken) {
  const { url, data, headers } = getOTPRequest(oldEmail, newEmail, phoneVerified, otpChannel, autoToken);
  return post(url, data, headers);
}

export function requestOTP(email, phone, otpChannel) {
  const { url, headers } = getPhoneVerificationOTP(email, phone, otpChannel);
  return get(url, {}, headers);
}

export function verifyOTP(otp, otpTicket, phoneNumber, account, autoToken) {
  return genericVerifyOTP(otp, otpTicket, phoneNumber, account, true, verifyOTPRequest, autoToken);
}

export function newUpdateEmailWithOTP(otp, newEmail, otpTicket, account, autoToken) {
  const { url, data, headers } = newUpdateEmailWithOTPRequest(
    otp,
    otpTicket?.authId,
    otpTicket?.userId,
    otpTicket?.backendHostName,
    account?.profile?.emailId,
    newEmail, account?.profile?.lastModifiedDate,
    account.encryptVerifiedPhoneToken,
    autoToken);
  return put(url, data, headers);
}

export function askForEmailPreferences(email, tokenGenerator) {
  const {url, data, headers} = askForEmailPreferencesRequest(email);
  return tokenGenerator(RECAPTCHA_EVENT).then((token) => {
    headers["x-recaptcha-token"] = token
    return post(url, data, headers);
  })
}

export function updateNewEmailPreferences(emailAddress, subscriptions, tokenGenerator) {
  const {url, data, headers} = updateNewEmailPreferencesRequest(emailAddress, subscriptions);
  return tokenGenerator(RECAPTCHA_EVENT).then((token) => {
    headers["x-recaptcha-token"] = token
    return put(url, data, headers);
  })
}

export function askForEmailChange(oldEmail, newEmail, phoneVerified, otpChannel, autoToken) {
  const {url, data, headers} = askForEmailChangeRequest(oldEmail, newEmail, phoneVerified, otpChannel, autoToken);
  return post(url, data, headers);
}

export function updateOldEmailPreferences(emailAddress, subscriptions) {
  const {url, data, headers} = updateOldEmailPreferencesRequest(emailAddress, subscriptions);
  return put(url, data, headers);
}

export function updateEmailWithOTP(otp, newEmail, otpTicket, account, autoToken) {
  const {url, data, headers} = updateEmailWithOTPRequest(
    otp,
    otpTicket?.authId,
    otpTicket?.userId,
    otpTicket?.backendHostName,
    account?.profile?.emailId,
    newEmail,
    account?.profile?.lastModifiedDate,
    autoToken);
  return put(url, data, headers);
}

export function updateCustomerProfile(account) {
  const { url, data, headers } = updateCustomerProfileRequest(account);
  return put(url, data, headers);
}

function genericVerifyOTP(otp, otpTicket, phoneNumber, account, is2faOptIn, requestGenerator, autoToken) {
  let phoneObj = [{
    primaryFlag: "Y"
  }]; // for user who do not have a phone number in their account, this is the request payload.

  if (account?.profilePhones?.phone) {
    phoneObj = account?.profilePhones?.phone;
  }

  const payload = {
    otpRequest: {
      ...(autoToken && {autoToken}),
      ...otpTicket,
      ...account?.profile?.name,
      loginId: account?.profile?.emailId,
      otp,
      phone: {
        ...phoneObj[0],
        number: phoneNumber,
        type: "MOBILE"
      },
      profileLastUpdateTimestamp: account?.profile?.lastModifiedDate,
      twoFactorAuthOptIn: is2faOptIn ? "Y" : "N",
      zipCode: account?.profile?.zipCode
    }
  };

  // eslint-disable-next-line dot-notation
  delete payload.otpRequest.phone["phoneVerified"];
  // eslint-disable-next-line dot-notation
  payload.otpRequest["backendHost"] = payload.otpRequest.backendHostName; // for some reason the verifyOTP api requires backendHost instead of backendHostName, hence this change
  const { url, data, headers } = requestGenerator(payload);

  return post(url, data, headers);
}

export function getAllSessions() {
  const { url, headers } = getAllSessionsRequest();
  return get(url, {}, headers);
}

export function signOutEverywhere() {
  const {url, headers} = logoutAllSessions();
  return post(url, {}, headers);
}

export function signOutCurrentDevice() {
  const {url, headers} = logoutCurrentDevice();
  return get(url, {}, headers);
}

export function signOutDevice(sessionId) {
  const tmxProfileId = getTMXSessionID();
  const {url, data, headers} = logoutDevice(sessionId, tmxProfileId);
  return post(url, data, headers);
}

export function unenrollFromPasswordless() {
  const {url, data, headers} = unenrollFromPasswordlessRequest();
  return post(url, data, headers);
}

export function getOTPv2(email, encryptedPayload, otpOption="email") {
  const { url, data, headers } = getOTPv2Request(email, encryptedPayload, otpOption);
  return post(url, data, headers);
}

export function passwordlessEnrollmentVerifyOTP(email, encryptedPayload, otp, passwordlessOptions) {
  const { url, data, headers } = passwordlessEnrollmentVerifyOTPRequest(email, encryptedPayload, otp, passwordlessOptions)
  return post(url, data, headers);
}

export function submitWebAuthnEnrollment(frState, webAuthnDeviceId) {
  const { url, data, headers } = submitWebAuthnEnrollmentRequest(frState, webAuthnDeviceId)
  return post(url, data, headers)
}

export function getOtpOptions(email) {
  const { url, data, headers } = getOtpOptionsRequest(email);
  return post(url, data, headers);
}

export function updatePasswordWithOTP(password, email, encryptedPayload, otp, origin) {
  const { url, data, headers } = updatePasswordWithOTPRequest(password, email, encryptedPayload, otp, origin);
  return post(url, data, headers);
}